import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { HumaTwinIcon } from '../../components/brand/HumaTwinLogo';

// ============================================================
// PUBLIC TERMS PAGE
// Shell page - legal content requires review
// ============================================================

export default function PublicTerms() {
  return (
    <div className="min-h-screen bg-white" data-testid="public-terms">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <HumaTwinIcon size={28} />
              <span className="font-heading text-lg font-bold text-gray-900">HumaTwin</span>
            </Link>
            <div className="flex items-center gap-4">
              <LanguageSwitcher variant="compact" />
              <Link to="/login">
                <Button variant="ghost" size="sm">Sign in</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <header className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-gray-900">
            Terms of Service
          </h1>
          <p className="mt-4 text-xl text-gray-600 max-w-2xl">
            The terms governing your use of HumaTwin
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Notice */}
          <div className="my-8 p-6 bg-amber-50 rounded-2xl border border-amber-200">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-900 mb-2">Terms Under Review</h3>
                <p className="text-amber-800 text-sm leading-relaxed">
                  The complete Terms of Service are currently being finalized for legal review. 
                  By using HumaTwin during this period, you agree to use the service in good faith 
                  and in accordance with the principles outlined in our Privacy page.
                </p>
              </div>
            </div>
          </div>

          {/* Summary of Key Terms */}
          <section className="py-8">
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">
              Key Principles
            </h2>
            
            <div className="space-y-6">
              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-3">Your Account</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-navy-600">•</span>
                    You are responsible for maintaining the security of your account credentials
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-navy-600">•</span>
                    You must provide accurate information when creating your account
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-navy-600">•</span>
                    You may delete your account and data at any time
                  </li>
                </ul>
              </div>

              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-3">Your Data</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-navy-600">•</span>
                    You own the personal information you provide to HumaTwin
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-navy-600">•</span>
                    You grant HumaTwin permission to process your information to provide the service
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-navy-600">•</span>
                    See our Privacy page for full details on data handling
                  </li>
                </ul>
              </div>

              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-3">Acceptable Use</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-navy-600">•</span>
                    Use HumaTwin for its intended purpose of building a personal representation
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-navy-600">•</span>
                    Do not attempt to misuse the service or circumvent its protections
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-navy-600">•</span>
                    Do not use HumaTwin to harm others or for illegal purposes
                  </li>
                </ul>
              </div>

              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-3">Service Limitations</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-navy-600">•</span>
                    HumaTwin is designed to represent you based on evidence, not to make decisions for you
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-navy-600">•</span>
                    Your Twin's responses are based on available evidence and may contain uncertainty
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-navy-600">•</span>
                    HumaTwin is not a substitute for professional advice (medical, legal, financial, etc.)
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-8 border-t border-gray-100">
            <p className="text-gray-600">
              Questions about these terms? Contact our support team through your account when logged in.
            </p>
            <div className="mt-6">
              <Link to="/privacy">
                <Button variant="outline">
                  View Privacy Policy
                </Button>
              </Link>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <HumaTwinIcon size={20} />
              <span className="text-sm text-gray-500">© {new Date().getFullYear()} HumaTwin</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/" className="hover:text-gray-900">Home</Link>
              <Link to="/privacy" className="hover:text-gray-900">Privacy</Link>
              <Link to="/login" className="hover:text-gray-900">Sign in</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
